import { yupResolver } from '@hookform/resolvers/yup';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import eyeOffIcon from '../../../../assets/images/icons/eye-off.svg';
import eyeIcon from '../../../../assets/images/icons/eye.svg';
import { getCurrentUser, AuthError } from 'aws-amplify/auth';
import { Form, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import Label from '../../../../components/Label';
import { FIELD_TEXT_MESSAGES } from '../../../../config';
import { useAppDispatch } from '../../../../store';
import { useErrorHandlers } from '../../../../utils/errorHandler';
import { PasswordValidationErrors } from '../../../app/components/PasswordValidationError';
import { updatePasswordThunk } from '../../../auth/api/authSlice';
import { UpdatePasswordSchema } from '../../schemas';
import alert from '../../../../utils/alert';
import Input from '../../../app/components/Input';
import './PasswordScreen.scss';

const PasswordScreen = () => {
  const dispatch = useAppDispatch();
  const { handleAuthError, handleGenericError } = useErrorHandlers();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [inputFieldType, setInputFieldType] = useState('password');
  const togglePasswordVisibility = (e: React.SyntheticEvent<EventTarget>) => {
    e.preventDefault();
    setInputFieldType(inputFieldType === 'text' ? 'password' : 'text');
  };
  const { t } = useTranslation();

  const {
    register,
    handleSubmit,
    formState: { errors, touchedFields, dirtyFields },
    trigger,
    reset,
  } = useForm({
    mode: 'all',
    criteriaMode: 'all',
    resolver: yupResolver(UpdatePasswordSchema),
  });

  const isFormValid = () => {
    const { currentPassword, password, confirmPassword } = errors;
    return (
      !!dirtyFields.currentPassword &&
      !!dirtyFields.password &&
      !!dirtyFields.confirmPassword &&
      !currentPassword &&
      !password &&
      !confirmPassword
    );
  };

  const onSubmitResetPassword = async (data: any) => {
    setIsSubmitting(true);
    try {
      const user = await getCurrentUser();

      if (!user || !data.currentPassword || !data.password) {
        alert.info(t(FIELD_TEXT_MESSAGES.ACCOUNT_PASSWORD_REQUIRED), {
          toastId: 'account-password-required-info',
        });
        setIsSubmitting(false);
        return;
      }

      await dispatch(
        updatePasswordThunk({
          oldPassword: data.currentPassword,
          newPassword: data.password,
        }),
      ).unwrap();

      alert.success(t(FIELD_TEXT_MESSAGES.ACCOUNT_PASSWORD_CHANGED), {
        toastId: 'account-password-changed-success',
      });

      setIsSubmitting(false);
      reset();
    } catch (error: any) {
      setIsSubmitting(false);

      if (error instanceof AuthError) {
        handleAuthError(error);
      } else {
        handleGenericError(error);
      }
    }
  };

  return (
    <div className='security-settings'>
      <h2>{t('Set new password')}</h2>
      <Form onSubmit={handleSubmit(onSubmitResetPassword)}>
        <p className='text-muted'>
          <i>{t('Enter the new password and confirmation to update your password.')} </i>
        </p>
        <Form.Group controlId='currentPassword' className='auth__input-group'>
          <Label className='auth__label'>{t('Current Password')}</Label>
          <div className='auth__input-group-input-container'>
            <Input
              type={inputFieldType}
              autoComplete='off'
              id='account-current-password'
              className={`auth__input auth__input-no-bootstrap-icon ${errors.currentPassword ? 'is-invalid' : ''}`}
              {...register('currentPassword')}
            />
            <button
              type='button'
              className='auth__input-group-icon'
              onClick={togglePasswordVisibility}
            >
              <img
                src={inputFieldType === 'password' ? eyeOffIcon : eyeIcon}
                width={20}
                height={20}
                alt='toggle password visibility'
              />
            </button>
          </div>
          {errors.currentPassword && (
            <Form.Control.Feedback type='invalid'>
              {errors.currentPassword.message ? t(errors.currentPassword.message) : ''}
            </Form.Control.Feedback>
          )}
        </Form.Group>
        <Form.Group controlId='newPassword' className='auth__input-group'>
          <Label className='auth__label'>{t('New password')}</Label>
          <div className='auth__input-group-input-container'>
            <Input
              type={inputFieldType}
              autoComplete='off'
              id='account-new-password'
              className={`auth__input auth__input-no-bootstrap-icon ${errors.password ? 'is-invalid' : ''}`}
              {...register('password', { onChange: () => trigger('confirmPassword') })}
            />
            <button
              type='button'
              className='auth__input-group-icon'
              onClick={togglePasswordVisibility}
            >
              <img
                src={inputFieldType === 'password' ? eyeOffIcon : eyeIcon}
                width={20}
                height={20}
                alt='toggle password visibility'
              />
            </button>
          </div>
        </Form.Group>
        <Form.Group controlId='confirmPassword' className='auth__input-group'>
          <Label className='auth__label'>{t('Confirm password')}</Label>
          <div className='auth__input-group-input-container'>
            <Input
              type={inputFieldType}
              autoComplete='off'
              id='account-password-confirm'
              className={`auth__input auth__input-no-bootstrap-icon ${errors.confirmPassword ? 'is-invalid' : ''}`}
              {...register('confirmPassword', { onChange: () => trigger('password') })}
            />
            <button
              type='button'
              className='auth__input-group-icon'
              onClick={togglePasswordVisibility}
            >
              <img
                src={inputFieldType === 'password' ? eyeOffIcon : eyeIcon}
                width={20}
                height={20}
                alt='toggle password visibility'
              />
            </button>
          </div>
        </Form.Group>
        <PasswordValidationErrors
          errors={errors}
          showPasswordGuide={
            !dirtyFields.password &&
            !dirtyFields.confirmPassword &&
            !touchedFields.password &&
            !touchedFields.confirmPassword
          }
        />
        <Form.Group>
          <button
            type='submit'
            className='btn btn-primary'
            disabled={isSubmitting || !isFormValid()}
          >
            {isSubmitting ? (
              <Spinner as='span' animation='border' size='sm' role='status' aria-hidden='true' />
            ) : (
              t('Update Password')
            )}
          </button>
        </Form.Group>
      </Form>
    </div>
  );
};

export default PasswordScreen;
